import { AreaState } from '../rules/suburb-subregion-edits';
import { anchorOf, displayName } from './map-geometry';
import {
  LABEL_FONT,
  LABEL_FONT_SETTINGS,
  LABEL_ZOOM,
  PAGE_DOT,
  SUBREGION_LINE,
  SUBURB_FILL,
  SUBURB_LABEL,
  SUBURB_LINE,
} from './map-style';
import { MapPage, MapRegion, MapSubRegion } from './map-types';

/**
 * The suburb map's deck.gl layers (#256, #260): the suburbs filled in their region's colour, sub-region outlines over
 * them, a dot on each suburb with a website page, and the names. Built afresh on every render from a snapshot of the
 * engine's state; deck.gl diffs them by id and updateTriggers.
 */

/** What the layers draw, as the engine has it at this render. */
export interface SuburbLayerView {
  stateCode: string | null;
  /** The suburbs after the view filters (hidden regions, unassigned only). */
  features: any[];
  areas: ReadonlyMap<string, AreaState>;
  regions: MapRegion[];
  subRegions: MapSubRegion[];
  pages: ReadonlyMap<string, MapPage>;
  selectedCode: string | null;
  hoverCode: string | null;
  /** Bumped on every change to `areas`: the colour accessors re-run when it moves. */
  revision: number;
  /** The sub-region being edited (Sub-region mode's, or Assign mode's pick): drawn even with outlines off. */
  editingSubRegion: string | null;
  showSubRegions: boolean;
  /** Outlines the Regions filter turned off (#273). */
  hiddenSubRegions: ReadonlySet<string>;
  showLabels: boolean;
  zoom: number;
}

export interface SuburbLayerEvents {
  hover(info: any): void;
  click(info: any): void;
}

/** Bottom to top: suburbs, sub-region outlines, page dots, names (null where a layer has nothing to draw). */
export function suburbLayers(deck: any, v: SuburbLayerView, on: SuburbLayerEvents): any[] {
  return [
    suburbFills(deck, v, on),
    ...subRegionOutlines(deck, v),
    pageDots(deck, v),
    suburbLabels(deck, v),
  ];
}

function suburbFills(deck: any, v: SuburbLayerView, on: SuburbLayerEvents): any {
  const code = (f: any): string => f.properties.code;
  return new deck.GeoJsonLayer({
    id: `suburbs-${v.stateCode}`,
    data: { type: 'FeatureCollection', features: v.features },
    pickable: true,
    stroked: true,
    filled: true,
    lineWidthUnits: 'pixels',
    // Covered suburbs get a strong outline: they're the ones Picaluna markets.
    getLineWidth: (f: any) => lineStyle(v, code(f)).width,
    getFillColor: (f: any) => fillColour(v, code(f)),
    getLineColor: (f: any) => lineStyle(v, code(f)).colour,
    updateTriggers: {
      getFillColor: [v.hoverCode, v.revision, v.regions],
      getLineColor: [v.hoverCode, v.selectedCode, v.revision],
      getLineWidth: [v.hoverCode, v.selectedCode, v.revision],
    },
    onHover: (info: any) => on.hover(info),
    onClick: (info: any) => on.click(info),
  });
}

function fillColour(v: SuburbLayerView, code: string): number[] {
  const row = v.areas.get(code);
  const hover = v.hoverCode === code;
  if (!row) return hover ? SUBURB_FILL.unassignedHover : SUBURB_FILL.unassigned;
  const rgb = v.regions.find((r) => r.id === row.regionId)?.rgb ?? SUBURB_FILL.regionFallback;
  const { alpha } = SUBURB_FILL;
  if (hover) return [...rgb, alpha.hover];
  return [...rgb, row.covered ? alpha.covered : alpha.assigned];
}

function lineStyle(v: SuburbLayerView, code: string): { colour: number[]; width: number } {
  if (v.selectedCode === code)
    return { colour: SUBURB_LINE.selected, width: SUBURB_LINE.width.selected };
  if (v.hoverCode === code) return { colour: SUBURB_LINE.hover, width: SUBURB_LINE.width.hover };
  if (v.areas.get(code)?.covered)
    return { colour: SUBURB_LINE.covered, width: SUBURB_LINE.width.covered };
  return { colour: SUBURB_LINE.other, width: SUBURB_LINE.width.other };
}

/**
 * One outline layer per shown sub-region (#260), over the region fills: its members among the drawn suburbs, 2 px in
 * its colour; the one being edited 3.5 px with a light fill, and last, so its line is on top where two sub-regions
 * share a border. Not pickable: clicks reach the suburbs.
 */
function subRegionOutlines(deck: any, v: SuburbLayerView): any[] {
  const editing = v.editingSubRegion;
  const shown = v.subRegions.filter(
    (s) => s.id === editing || (v.showSubRegions && !v.hiddenSubRegions.has(s.id))
  );
  if (!shown.length) return [];
  const members = new Map<string, any[]>(shown.map((s) => [s.id, []]));
  for (const f of v.features) {
    for (const id of v.areas.get(f.properties.code)?.subRegions ?? []) members.get(id)?.push(f);
  }
  const ordered = [
    ...shown.filter((s) => s.id !== editing),
    ...shown.filter((s) => s.id === editing),
  ];
  return ordered
    .filter((s) => members.get(s.id)?.length)
    .map((s) => {
      const active = s.id === editing;
      return new deck.GeoJsonLayer({
        id: `subregion-${s.id}`,
        data: { type: 'FeatureCollection', features: members.get(s.id) },
        pickable: false,
        stroked: true,
        filled: active,
        lineWidthUnits: 'pixels',
        getLineWidth: active ? SUBREGION_LINE.activeWidth : SUBREGION_LINE.width,
        getLineColor: [...s.rgb, SUBREGION_LINE.alpha],
        getFillColor: [...s.rgb, SUBREGION_LINE.fillAlpha],
        updateTriggers: { getLineColor: [s.rgb] },
      });
    });
}

/** A dot on each suburb that has a website page: those can't be moved out of their region or uncovered. */
function pageDots(deck: any, v: SuburbLayerView): any {
  const withPages = v.features.filter((f) => v.pages.has(f.properties.code));
  if (!withPages.length) return null;
  return new deck.ScatterplotLayer({
    id: 'pages',
    data: withPages,
    getPosition: (f: any) => anchorOf(f),
    getRadius: PAGE_DOT.radius,
    radiusUnits: 'pixels',
    getFillColor: PAGE_DOT.fill,
    getLineColor: PAGE_DOT.line,
    lineWidthMinPixels: PAGE_DOT.lineWidth,
    stroked: true,
    pickable: false,
  });
}

/** Suburb names from street-ish zoom; bigger suburbs win the collision, so the map reads as areas, not noise. */
function suburbLabels(deck: any, v: SuburbLayerView): any {
  if (!v.showLabels || v.zoom < LABEL_ZOOM) return null;
  return new deck.TextLayer({
    id: 'labels',
    data: v.features,
    getPosition: (f: any) => anchorOf(f),
    getText: (f: any) => displayName(f.properties.name),
    getSize: SUBURB_LABEL.size,
    sizeUnits: 'pixels',
    getColor: SUBURB_LABEL.colour,
    outlineWidth: 4,
    outlineColor: SUBURB_LABEL.outline,
    fontSettings: LABEL_FONT_SETTINGS,
    fontFamily: LABEL_FONT,
    fontWeight: 600,
    characterSet: 'auto',
    getTextAnchor: 'middle',
    getAlignmentBaseline: 'center',
    getPixelOffset: (f: any) => (v.pages.has(f.properties.code) ? SUBURB_LABEL.belowDot : [0, 0]),
    extensions: [new deck.CollisionFilterExtension()],
    collisionEnabled: true,
    getCollisionPriority: (f: any) => Math.log1p(f.properties.areaKm2 || 0),
    collisionTestProps: { sizeScale: 1.6 },
    pickable: false,
  });
}
